import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WebDevelopmentSection = () => {
  const [activeComparison, setActiveComparison] = useState('before');

  const beforeAfterData = {
    before: {
      loadTime: '8.2s',
      mobileScore: '42/100',
      seoScore: '35/100',
      conversionRate: '1.2%',
      bounceRate: '78%',
      screenshot: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop'
    },
    after: {
      loadTime: '1.8s',
      mobileScore: '98/100',
      seoScore: '95/100',
      conversionRate: '4.7%',
      bounceRate: '32%',
      screenshot: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
    }
  };

  const technologies = [
    { name: 'React', icon: 'Code', category: 'Frontend' },
    { name: 'Node.js', icon: 'Server', category: 'Backend' },
    { name: 'MongoDB', icon: 'Database', category: 'Database' },
    { name: 'AWS', icon: 'Cloud', category: 'Cloud' },
    { name: 'TypeScript', icon: 'FileCode', category: 'Language' },
    { name: 'Next.js', icon: 'Zap', category: 'Framework' }
  ];

  const projectTypes = [
    {
      title: 'E-commerce Platforms',
      description: 'Custom online stores with payment integration, inventory management, and analytics.',
      features: ['Payment Gateway Integration', 'Inventory Management', 'Order Tracking', 'Analytics Dashboard'],
      icon: 'ShoppingCart',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Corporate Websites',
      description: 'Professional business websites that establish credibility and drive conversions.',
      features: ['Responsive Design', 'CMS Integration', 'SEO Optimization', 'Contact Forms'],
      icon: 'Building',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Web Applications',
      description: 'Custom web apps tailored to your business processes and user needs.',
      features: ['User Authentication', 'Real-time Updates', 'API Integration', 'Cloud Hosting'],
      icon: 'Globe',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const performanceMetrics = [
    { label: 'Average Load Time Improvement', value: '65%', icon: 'Zap' },
    { label: 'Mobile Performance Score', value: '98/100', icon: 'Smartphone' },
    { label: 'SEO Score Improvement', value: '+60 points', icon: 'TrendingUp' },
    { label: 'Conversion Rate Increase', value: '290%', icon: 'Target' }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Code" size={16} />
            <span>Web Development</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            High-Performance Web Solutions
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We build fast, secure, and scalable websites that not only look stunning but deliver exceptional user experiences and drive business results.
          </p>
        </div>

        {/* Before/After Comparison */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Website Transformation Showcase
          </h3>
          
          <div className="bg-card rounded-2xl p-8 brand-shadow">
            <div className="flex justify-center mb-8">
              <div className="bg-muted rounded-lg p-1">
                <button
                  onClick={() => setActiveComparison('before')}
                  className={`px-6 py-2 rounded-md font-semibold brand-transition ${
                    activeComparison === 'before'
                      ? 'bg-destructive text-destructive-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Before
                </button>
                <button
                  onClick={() => setActiveComparison('after')}
                  className={`px-6 py-2 rounded-md font-semibold brand-transition ${
                    activeComparison === 'after' ?'bg-success text-success-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  After
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Screenshot */}
              <div className="relative">
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  <img 
                    src={beforeAfterData?.[activeComparison]?.screenshot}
                    alt={`Website ${activeComparison} transformation`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-semibold ${
                  activeComparison === 'before' 
                    ? 'bg-destructive text-destructive-foreground' :'bg-success text-success-foreground'
                }`}>
                  {activeComparison === 'before' ? 'Before' : 'After'}
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-surface rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Load Time</p>
                    <p className={`text-2xl font-bold ${
                      activeComparison === 'before' ? 'text-destructive' : 'text-success'
                    }`}>
                      {beforeAfterData?.[activeComparison]?.loadTime}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-surface rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Mobile Score</p>
                    <p className={`text-2xl font-bold ${
                      activeComparison === 'before' ? 'text-destructive' : 'text-success'
                    }`}>
                      {beforeAfterData?.[activeComparison]?.mobileScore}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-surface rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">SEO Score</p>
                    <p className={`text-2xl font-bold ${
                      activeComparison === 'before' ? 'text-destructive' : 'text-success'
                    }`}>
                      {beforeAfterData?.[activeComparison]?.seoScore}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-surface rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Conversion Rate</p>
                    <p className={`text-2xl font-bold ${
                      activeComparison === 'before' ? 'text-destructive' : 'text-success'
                    }`}>
                      {beforeAfterData?.[activeComparison]?.conversionRate}
                    </p>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-muted-foreground mb-2">Bounce Rate</p>
                  <p className={`text-3xl font-bold ${
                    activeComparison === 'before' ? 'text-destructive' : 'text-success'
                  }`}>
                    {beforeAfterData?.[activeComparison]?.bounceRate}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Types */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Types of Web Solutions We Build
          </h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {projectTypes?.map((project, index) => (
              <div key={index} className="bg-card rounded-2xl p-8 brand-shadow brand-hover-lift brand-transition">
                <div className={`w-16 h-16 bg-gradient-to-r ${project?.color} rounded-xl flex items-center justify-center mb-6`}>
                  <Icon name={project?.icon} size={32} className="text-white" />
                </div>
                
                <h4 className="text-xl font-bold text-foreground mb-4">{project?.title}</h4>
                <p className="text-muted-foreground mb-6">{project?.description}</p>
                
                <div className="space-y-3">
                  {project?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <Icon name="Check" size={16} className="text-success" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technologies */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Technologies We Master
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies?.map((tech, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-accent group-hover:text-accent-foreground brand-transition">
                  <Icon name={tech?.icon} size={32} />
                </div>
                <p className="font-semibold text-foreground">{tech?.name}</p>
                <p className="text-xs text-muted-foreground">{tech?.category}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 lg:p-12 mb-16">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground text-center mb-12">
            Performance That Speaks for Itself
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {performanceMetrics?.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={metric?.icon} size={32} className="text-accent-foreground" />
                </div>
                <p className="text-3xl font-bold text-foreground mb-2">{metric?.value}</p>
                <p className="text-muted-foreground">{metric?.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Development Process */}
        <div className="bg-card rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl lg:text-3xl font-bold text-foreground text-center mb-12">
            Our Development Process
          </h3>
          
          <div className="grid md:grid-cols-5 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Requirements gathering & technical planning', duration: '1-2 weeks' },
              { step: '02', title: 'Design', desc: 'UI/UX design & prototype creation', duration: '2-3 weeks' },
              { step: '03', title: 'Development', desc: 'Frontend & backend development', duration: '4-8 weeks' },
              { step: '04', title: 'Testing', desc: 'Quality assurance & performance testing', duration: '1-2 weeks' },
              { step: '05', title: 'Launch', desc: 'Deployment & post-launch support', duration: '1 week' }
            ]?.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {phase?.step}
                </div>
                <h4 className="font-bold text-foreground mb-2">{phase?.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{phase?.desc}</p>
                <p className="text-xs text-accent font-medium">{phase?.duration}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button 
            variant="default" 
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            iconName="Code"
            iconPosition="left"
          >
            Start Web Project
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WebDevelopmentSection;